import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state, queryAssignedElements } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { dispatch } from '../../utils/event-helpers';

export type NavigationSize = 'md' | 'lg';
export type PaginationStyle = 'framed' | 'unframed';
export type CarouselHeight = 'normalized' | 'auto';

/**
 * A horizontally paginated content carousel with overlaid arrow navigation
 * and pagination dots.
 *
 * Accepts 2 or more `<asc-slide>` elements via its default slot. Each slide
 * may contain arbitrary HTML (images, text, cards, etc.). The carousel always
 * fills its parent's inline size (`width: 100%`). All responsive adaptations
 * use `@container` queries scoped to the carousel's own width — never
 * `@media` viewport queries.
 *
 * Slide height is determined by the tallest slide; shorter slides vertically
 * center their content. Arrow buttons and the pagination dot group are
 * absolutely positioned over the slide area (matching the Figma design).
 * The slide track translates via a CSS-driven `--_active-index` inline
 * custom property — no JavaScript width measurement required.
 *
 * @element asc-carousel
 *
 * @fires asc-slide-change - Fired after the visible slide changes via arrow or
 *   dot interaction (not on programmatic property changes).
 *   Detail: `{ index: number; total: number }`
 *
 * @slot - Required. Accepts 2 or more `<asc-slide>` elements as direct
 *   children. Fewer than 2 slides suppresses navigation and emits a warning.
 *
 * @csspart base        - Root `<section>` (position: relative) — top-level hook.
 * @csspart viewport    - `<div>` with overflow:hidden that clips the slide track.
 * @csspart track       - Flex-row `<div>` that translates on navigation.
 * @csspart prev-button - Previous-slide arrow `<button>`.
 * @csspart next-button - Next-slide arrow `<button>`.
 * @csspart pagination  - Pagination dot group `<div>`.
 * @csspart dot         - Individual pagination dot `<button>`.
 *
 * @cssprop [--asc-carousel-arrow-size] - Override arrow button width/height for both sizes.
 * @cssprop [--asc-carousel-arrow-icon-size] - Override arrow SVG icon size.
 * @cssprop [--asc-carousel-arrow-bg] - Arrow background. Default: `var(--asc-alpha-white-90)`
 * @cssprop [--asc-carousel-arrow-bg-hover] - Arrow background on hover. Default: `var(--asc-alpha-white-100)`
 * @cssprop [--asc-carousel-arrow-bg-disabled] - Arrow background when disabled. Default: `var(--asc-alpha-white-50)`
 * @cssprop [--asc-carousel-arrow-color] - Arrow icon stroke color. Default: `var(--asc-fg-secondary)`
 * @cssprop [--asc-carousel-arrow-color-disabled] - Arrow icon color when disabled. Default: `var(--asc-fg-disabled)`
 * @cssprop [--asc-carousel-arrow-border-radius] - Arrow border radius. Default: `var(--asc-radius-full)`
 * @cssprop [--asc-carousel-arrow-offset] - Distance from carousel edge to arrow center.
 * @cssprop [--asc-carousel-dot-size] - Override dot diameter for both sizes.
 * @cssprop [--asc-carousel-dot-gap] - Gap between pagination dots.
 * @cssprop [--asc-carousel-dot-color] - Inactive dot color. Default: `var(--asc-bg-quaternary)`
 * @cssprop [--asc-carousel-dot-color-active] - Active dot color. Default: `var(--asc-fg-brand-primary)`
 * @cssprop [--asc-carousel-dot-border-radius] - Dot border radius. Default: `var(--asc-radius-full)`
 * @cssprop [--asc-carousel-pagination-offset] - Distance from carousel bottom to pagination row.
 * @cssprop [--asc-carousel-pagination-bg] - Framed pagination background. Default: `var(--asc-alpha-white-90)`
 * @cssprop [--asc-carousel-pagination-padding] - Framed pagination inner padding.
 * @cssprop [--asc-carousel-pagination-border-radius] - Framed pagination border radius. Default: `var(--asc-radius-full)`
 * @cssprop [--asc-carousel-transition-duration] - Slide transition duration. Default: `300ms`
 * @cssprop [--asc-carousel-transition-easing] - Slide transition easing. Default: `ease`
 * @cssprop [--asc-carousel-height-transition-duration] - Viewport height transition duration in `auto` mode. Default: `var(--asc-carousel-transition-duration, 300ms)`
 */
@customElement('asc-carousel')
export class AscCarousel extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      /*
       * Enables @container rules inside this shadow DOM and provides the cqi
       * unit context that <asc-slide> uses for flex: 0 0 100cqi.
       */
      container-type: inline-size;
    }

    /* ── Root container ───────────────────────────────────────── */
    .base {
      position: relative;
    }

    /* ── Viewport: clips the sliding track ────────────────────── */
    .viewport {
      overflow: hidden;
      width: 100%;
      cursor: grab;
      user-select: none;
    }

    .viewport:active {
      cursor: grabbing;
    }

    /* ── Track: slides in a horizontal flex row ───────────────── */
    .track {
      display: flex;
      align-items: stretch; /* normalized: all slides grow to height of tallest */
      transform: translateX(calc(var(--_active-index, 0) * -100cqi));
      transition:
        transform var(--asc-carousel-transition-duration, 300ms)
        var(--asc-carousel-transition-easing, ease);
      will-change: transform;
    }

    /* ── Auto height mode ─────────────────────────────────────── */
    :host([height='auto']) .track {
      align-items: flex-start; /* each slide takes its intrinsic height */
    }

    :host([height='auto']) .viewport {
      transition:
        height var(--asc-carousel-height-transition-duration,
          var(--asc-carousel-transition-duration, 300ms))
        var(--asc-carousel-transition-easing, ease);
      overflow: hidden;
    }

    /* ── Arrow buttons ────────────────────────────────────────── */
    .prev-btn,
    .next-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;

      /* Layout */
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      /* Sizing — md default: 36px (no spacing token matches 36px) */
      width: var(--asc-carousel-arrow-size, 36px); /* no-token: 36px arrow size for md navigation */
      height: var(--asc-carousel-arrow-size, 36px); /* no-token: 36px arrow size for md navigation */
      padding: var(--asc-spacing-md);

      /* Visual */
      background: var(--asc-carousel-arrow-bg, var(--asc-alpha-white-90));
      color: var(--asc-carousel-arrow-color, var(--asc-fg-secondary));
      border: none;
      border-radius: var(--asc-carousel-arrow-border-radius, var(--asc-radius-full));
      backdrop-filter: blur(4px); /* no-token: design-spec backdrop-blur value */
      cursor: pointer;
      transition: background var(--asc-transition-base);
    }

    .prev-btn {
      left: var(--asc-carousel-arrow-offset, var(--asc-spacing-xl)); /* md: 16px from edge */
    }

    .next-btn {
      right: var(--asc-carousel-arrow-offset, var(--asc-spacing-xl)); /* md: 16px from edge */
    }

    .prev-btn:hover:not(:disabled),
    .next-btn:hover:not(:disabled) {
      background: var(--asc-carousel-arrow-bg-hover, var(--asc-alpha-white-100));
    }

    .prev-btn:disabled,
    .next-btn:disabled {
      background: var(--asc-carousel-arrow-bg-disabled, var(--asc-alpha-white-50));
      color: var(--asc-carousel-arrow-color-disabled, var(--asc-fg-disabled));
      cursor: not-allowed;
    }

    .prev-btn:focus-visible,
    .next-btn:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* Arrow SVG icon */
    .arrow-icon {
      display: block;
      flex-shrink: 0;
      pointer-events: none;
      /* md icon: 10.667 × 10 */
      width: 10.667px; /* no-token: icon width derived from 16:15 SVG viewBox at md scale */
      height: 10px; /* no-token: icon height derived from 16:15 SVG viewBox at md scale */
    }

    /* ── lg navigation size overrides ────────────────────────── */
    :host([navigation-size='lg']) .prev-btn,
    :host([navigation-size='lg']) .next-btn {
      /* lg: 44px button (no spacing token matches 44px) */
      width: var(--asc-carousel-arrow-size, 44px); /* no-token: 44px arrow size for lg navigation */
      height: var(--asc-carousel-arrow-size, 44px); /* no-token: 44px arrow size for lg navigation */
    }

    :host([navigation-size='lg']) .prev-btn {
      left: var(--asc-carousel-arrow-offset, var(--asc-spacing-2xl)); /* lg: 20px from edge */
    }

    :host([navigation-size='lg']) .next-btn {
      right: var(--asc-carousel-arrow-offset, var(--asc-spacing-2xl)); /* lg: 20px from edge */
    }

    :host([navigation-size='lg']) .arrow-icon {
      /* lg icon: 16 × 15 (1:1 with SVG viewBox) */
      width: var(--asc-carousel-arrow-icon-size, var(--asc-spacing-xl)); /* 16px */
      height: 15px; /* no-token: matches SVG viewBox height at 1:1 scale */
    }

    /* ── Pagination dot group ─────────────────────────────────── */
    .pagination {
      position: absolute;
      bottom: var(--asc-carousel-pagination-offset, var(--asc-spacing-xl)); /* 16px from bottom */
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;

      display: flex;
      align-items: center;
      justify-content: center;
      /* md dot gap: 12px = --asc-spacing-lg */
      gap: var(--asc-carousel-dot-gap, var(--asc-spacing-lg));
    }

    /* Framed: pill background container — md */
    :host([pagination-style='framed']) .pagination {
      background: var(--asc-carousel-pagination-bg, var(--asc-alpha-white-90));
      /* md padding: 8px = --asc-spacing-md */
      padding: var(--asc-carousel-pagination-padding, var(--asc-spacing-md));
      border-radius: var(--asc-carousel-pagination-border-radius, var(--asc-radius-full));
      backdrop-filter: blur(4px); /* no-token: design-spec backdrop-blur value */
    }

    /* lg dot gap override */
    :host([navigation-size='lg']) .pagination {
      /* lg dot gap: 16px = --asc-spacing-xl */
      gap: var(--asc-carousel-dot-gap, var(--asc-spacing-xl));
    }

    /* lg framed padding override */
    :host([navigation-size='lg'][pagination-style='framed']) .pagination {
      /* lg padding: 12px = --asc-spacing-lg */
      padding: var(--asc-carousel-pagination-padding, var(--asc-spacing-lg));
    }

    /* ── Individual pagination dots ───────────────────────────── */
    [part='dot'] {
      display: block;
      flex-shrink: 0;
      /* md dot: 8px = --asc-spacing-md */
      width: var(--asc-carousel-dot-size, var(--asc-spacing-md));
      height: var(--asc-carousel-dot-size, var(--asc-spacing-md));
      padding: 0;

      background: var(--asc-carousel-dot-color, var(--asc-bg-quaternary));
      border: none;
      border-radius: var(--asc-carousel-dot-border-radius, var(--asc-radius-full));
      cursor: pointer;
      transition: background var(--asc-transition-base);
    }

    [part='dot'][aria-current='true'] {
      background: var(--asc-carousel-dot-color-active, var(--asc-fg-brand-primary));
    }

    [part='dot']:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* lg dot size: 10px (no spacing token matches 10px) */
    :host([navigation-size='lg']) [part='dot'] {
      width: var(--asc-carousel-dot-size, 10px); /* no-token: 10px dot size for lg navigation */
      height: var(--asc-carousel-dot-size, 10px); /* no-token: 10px dot size for lg navigation */
    }

    /* ── Visually hidden live region for screen reader announcements ── */
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* ── Container query: compact layout at narrow container widths ── */
    @container (max-width: 480px) {
      /* Reduce arrow size slightly on narrow containers */
      .prev-btn,
      .next-btn {
        width: var(--asc-carousel-arrow-size, 32px); /* no-token: compact arrow size */
        height: var(--asc-carousel-arrow-size, 32px);
      }

      :host([navigation-size='lg']) .prev-btn,
      :host([navigation-size='lg']) .next-btn {
        width: var(--asc-carousel-arrow-size, 36px); /* no-token: compact lg arrow size */
        height: var(--asc-carousel-arrow-size, 36px);
      }
    }

    /* ── Reduced motion ───────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .track {
        transition-duration: 0ms;
      }
    }
  `;

  /** Controls the scale of arrow buttons and pagination dots. */
  @property({ type: String, reflect: true, attribute: 'navigation-size' })
  navigationSize: NavigationSize = 'md';

  /** Controls whether the pagination dot group renders with a visible background container. */
  @property({ type: String, reflect: true, attribute: 'pagination-style' })
  paginationStyle: PaginationStyle = 'unframed';

  /** When true, navigation wraps from the last slide back to the first and vice versa. */
  @property({ type: Boolean, reflect: true })
  loop = false;

  /**
   * Controls how carousel height relates to slide content.
   * - `normalized` — all slides stretch to the tallest; shorter slides center vertically.
   * - `auto` — viewport animates to match the active slide's intrinsic height.
   */
  @property({ type: String, reflect: true })
  height: CarouselHeight = 'normalized';

  @state() private _activeIndex = 0;
  @state() private _slideCount = 0;
  @state() private _viewportHeight: number | null = null;

  @query('[part="prev-button"]') private _prevBtn!: HTMLButtonElement;
  @query('[part="next-button"]') private _nextBtn!: HTMLButtonElement;

  @queryAssignedElements({ selector: 'asc-slide' })
  private _slides!: Array<Element>;

  private _resizeObserver: ResizeObserver | null = null;

  private _handleSlotChange(): void {
    const count = this._slides.length;
    this._slideCount = count;

    if (count > 0 && count < 2) {
      console.warn('asc-carousel: a minimum of 2 slides is required.');
    }

    // Clamp active index in case slides were removed
    if (this._activeIndex >= count) {
      this._activeIndex = Math.max(0, count - 1);
    }

    this._updateSlideAria();
    this._syncViewportHeight();
  }

  private _prev(): void {
    if (this._slideCount < 2) return;
    this._activeIndex = this.loop
      ? (this._activeIndex - 1 + this._slideCount) % this._slideCount
      : Math.max(0, this._activeIndex - 1);
    this._afterTransition();
  }

  private _next(): void {
    if (this._slideCount < 2) return;
    this._activeIndex = this.loop
      ? (this._activeIndex + 1) % this._slideCount
      : Math.min(this._slideCount - 1, this._activeIndex + 1);
    this._afterTransition();
  }

  private _goTo(index: number): void {
    if (index === this._activeIndex) return;
    this._activeIndex = index;
    this._afterTransition();
  }

  private _afterTransition(): void {
    this._updateSlideAria();
    this._syncViewportHeight();
    // When loop=false and navigation lands on a boundary, the button just
    // activated is about to become disabled. Move focus to the opposite button
    // now — before Lit re-renders and disables the current one — so the user
    // doesn't lose keyboard focus entirely.
    if (!this.loop) {
      const focused = this.shadowRoot?.activeElement;
      if (this._activeIndex === this._slideCount - 1 && focused === this._nextBtn) {
        this._prevBtn?.focus();
      } else if (this._activeIndex === 0 && focused === this._prevBtn) {
        this._nextBtn?.focus();
      }
    }
    dispatch<{ index: number; total: number }>(this, 'asc-slide-change', {
      detail: { index: this._activeIndex, total: this._slideCount },
    });
  }

  private _updateSlideAria(): void {
    this._slides.forEach((slide, i) => {
      const isActive = i === this._activeIndex;
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Slide ${i + 1} of ${this._slideCount}`);
      if (isActive) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        // inert fully prevents focus and interaction on off-screen slides,
        // preventing keyboard users from tabbing into hidden slide content.
        slide.setAttribute('inert', '');
      }
    });
  }

  /* ── Auto-height viewport sync ───────────────────────────── */

  private _syncViewportHeight(): void {
    if (this.height !== 'auto') {
      this._teardownResizeObserver();
      this._viewportHeight = null;
      return;
    }

    const activeSlide = this._slides[this._activeIndex] as HTMLElement | undefined;
    if (!activeSlide) {
      this._viewportHeight = null;
      return;
    }

    this._viewportHeight = activeSlide.offsetHeight;
    this._observeActiveSlide(activeSlide);
  }

  private _observeActiveSlide(slide: HTMLElement): void {
    this._teardownResizeObserver();
    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const target = entry.target as HTMLElement;
        this._viewportHeight = target.offsetHeight;
      }
    });
    this._resizeObserver.observe(slide);
  }

  private _teardownResizeObserver(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('keydown', this._handleKeyDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this._handleKeyDown);
    this._teardownResizeObserver();
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    if (changed.has('height')) {
      this._syncViewportHeight();
    }
  }

  /* ── Swipe / pointer gesture tracking ───────────────────── */
  private _pointerStartX = 0;
  private _pointerStartY = 0;
  private _isSwiping = false;
  private _pointerId: number | null = null;

  /** Minimum horizontal distance (px) to count as a swipe. */
  private static readonly _SWIPE_THRESHOLD = 40;

  /** Maximum vertical deviation before a swipe is cancelled (scroll takes over). */
  private static readonly _SWIPE_MAX_Y = 60;

  private _handlePointerDown(e: PointerEvent): void {
    // Only track primary pointer (first finger / mouse left-button)
    if (!e.isPrimary) return;
    this._pointerStartX = e.clientX;
    this._pointerStartY = e.clientY;
    this._isSwiping = true;
    this._pointerId = e.pointerId;

    // Capture pointer so we receive pointermove/up even if it leaves the element
    const viewport = e.currentTarget as HTMLElement;
    viewport.setPointerCapture(e.pointerId);
  }

  private _handlePointerMove(e: PointerEvent): void {
    if (!this._isSwiping || e.pointerId !== this._pointerId) return;

    // Cancel swipe if significant vertical scroll is detected
    const deltaY = Math.abs(e.clientY - this._pointerStartY);
    if (deltaY > AscCarousel._SWIPE_MAX_Y) {
      this._isSwiping = false;
    }
  }

  private _handlePointerUp(e: PointerEvent): void {
    if (!this._isSwiping || e.pointerId !== this._pointerId) {
      this._isSwiping = false;
      this._pointerId = null;
      return;
    }

    const deltaX = e.clientX - this._pointerStartX;
    this._isSwiping = false;
    this._pointerId = null;

    if (Math.abs(deltaX) >= AscCarousel._SWIPE_THRESHOLD) {
      if (deltaX < 0) {
        this._next();
      } else {
        this._prev();
      }
    }
  }

  private _handleKeyDown(e: KeyboardEvent): void {
    // Do not intercept arrow keys when focus is inside a text-editing element
    // (inputs, textareas, selects, or contenteditable) to preserve their
    // native cursor/selection behaviour.
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._next();
    }
  }

  override render() {
    const hasSlides = this._slideCount >= 2;
    const prevDisabled = !this.loop && this._activeIndex === 0;
    const nextDisabled = !this.loop && this._activeIndex === this._slideCount - 1;

    return html`
      <section
        class="base"
        part="base"
        aria-roledescription="carousel"
        aria-label="Content carousel"
      >
        <div
          class="viewport"
          part="viewport"
          @pointerdown=${this._handlePointerDown}
          @pointermove=${this._handlePointerMove}
          @pointerup=${this._handlePointerUp}
          @pointercancel=${this._handlePointerUp}
          @dragstart=${(e: Event) => e.preventDefault()}
          style="touch-action: pan-y pinch-zoom;${this.height === 'auto' && this._viewportHeight != null ? ` height: ${this._viewportHeight}px;` : ''}"
        >
          <div
            class="track"
            part="track"
            style="--_active-index: ${this._activeIndex}"
          >
            <slot @slotchange=${this._handleSlotChange}></slot>
          </div>
        </div>

        ${hasSlides
          ? html`
              <button
                type="button"
                class="prev-btn"
                part="prev-button"
                aria-label="Previous slide"
                ?disabled=${prevDisabled}
                aria-disabled=${prevDisabled ? 'true' : 'false'}
                @click=${this._prev}
              >
                <svg
                  class="arrow-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 15"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M7.5 0C7.36667 0 7.24048 0.0518181 7.14648 0.146484L0.146484 7.14648C0.0999 7.19305 0.0634 7.24773 0.0381 7.30957C0.0361 7.31486 0.0332 7.3212 0.0312 7.32715C0.0106 7.38115 0 7.44067 0 7.5C0 7.55795 0.0106 7.61592 0.0312 7.67188C0.0326 7.67583 0.0338 7.67932 0.0352 7.68262C0.0631 7.75186 0.1009 7.80689 0.1475 7.85352L7.14648 14.8535C7.24048 14.9482 7.36667 15 7.5 15C7.63333 15 7.75952 14.9482 7.85352 14.8535C8.04807 14.659 8.04785 14.3418 7.85352 14.1465L1.707 7.99902H15.5C15.776 7.99969 16 7.776 16 7.5C16 7.224 15.776 7 15.5 7H1.707L7.85352 0.853516C7.94752 0.759516 8 0.633333 8 0.5C8 0.366667 7.94818 0.240484 7.85352 0.146484C7.75952 0.051818 7.63333 0 7.5 0Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              <button
                type="button"
                class="next-btn"
                part="next-button"
                aria-label="Next slide"
                ?disabled=${nextDisabled}
                aria-disabled=${nextDisabled ? 'true' : 'false'}
                @click=${this._next}
              >
                <svg
                  class="arrow-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 15"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8.5 0C8.63333 0 8.75952 0.0518181 8.85352 0.146484L15.8535 7.14648C15.9001 7.19305 15.9366 7.24773 15.9619 7.30957C15.9639 7.31486 15.9668 7.3212 15.9688 7.32715C15.9894 7.38115 16 7.44067 16 7.5C16 7.55795 15.9894 7.61592 15.9688 7.67188C15.9674 7.67583 15.9662 7.67932 15.9648 7.68262C15.9369 7.75186 15.8991 7.80689 15.8525 7.85352L8.85352 14.8535C8.75952 14.9482 8.63333 15 8.5 15C8.36667 15 8.24048 14.9482 8.14648 14.8535C7.95193 14.659 7.95215 14.3418 8.14648 14.1465L14.293 7.99902H0.5C0.224 7.99969 0 7.776 0 7.5C0 7.224 0.224 7 0.5 7H14.293L8.14648 0.853516C8.05248 0.759516 8 0.633333 8 0.5C8 0.366667 8.05182 0.240484 8.14648 0.146484C8.24048 0.051818 8.36667 0 8.5 0Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              <div
                class="pagination"
                part="pagination"
                role="group"
                aria-label="Slide navigation"
              >
                ${repeat(
                  Array.from({ length: this._slideCount }, (_, i) => i),
                  (i) => i,
                  (i) => html`
                    <button
                      type="button"
                      part="dot"
                      aria-label=${`Go to slide ${i + 1}`}
                      aria-current=${i === this._activeIndex ? 'true' : 'false'}
                      @click=${() => this._goTo(i)}
                    ></button>
                  `,
                )}
              </div>

              <div class="sr-only" aria-live="polite" aria-atomic="true">
                Slide ${this._activeIndex + 1} of ${this._slideCount}
              </div>
            `
          : nothing}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-carousel': AscCarousel;
  }
}
